/**
 * Keyset pagination as a hook.
 *
 * The backend uses cursor pagination, which gives no random page access — so
 * "previous" cannot be computed and has to be remembered. This keeps a stack of the
 * cursors already visited, which is what makes Previous work at all.
 *
 * Resetting that stack whenever the FILTERS change is the subtle part: a cursor
 * encodes a position in one specific ordered result set, so carrying it across a
 * filter change would page into the middle of a different list and show a
 * confusing, apparently random slice.
 */
import { useCallback, useEffect, useState } from "react";

export interface UsePagedQueryResult {
  cursor: string | undefined;
  /** True when a previous page has been visited and can be returned to. */
  canGoBack: boolean;
  goNext: (nextCursor: string | null) => void;
  goPrevious: () => void;
  reset: () => void;
}

/**
 * The visited-cursor stack, plus the filter key it belongs to.
 *
 * Both in ONE state object rather than two, and the trail is state rather than a
 * ref. A ref would not trigger a re-render when the stack changes, so the Previous
 * button's enabled state could lag behind reality — and reading `ref.current`
 * during render to compute it is exactly the pattern React's lint rule flags.
 *
 * Storing `filterKey` alongside lets the reset happen during render (the
 * "adjusting state when a prop changes" pattern) instead of in an effect, which
 * avoids rendering one frame of the wrong page.
 */
interface PageState {
  cursor: string | undefined;
  trail: Array<string | undefined>;
  filterKey: string;
}

export function usePagedQuery(filterKey: string): UsePagedQueryResult {
  const [state, setState] = useState<PageState>({
    cursor: undefined,
    trail: [],
    filterKey,
  });

  /**
   * Filters changed: discard the trail.
   *
   * A cursor encodes a position in one specific ordered result set, so carrying it
   * across a filter change pages into the middle of a different list and shows an
   * apparently random slice. Done during render, which React supports for exactly
   * this case and which avoids the extra render an effect would cause.
   */
  if (state.filterKey !== filterKey) {
    setState({ cursor: undefined, trail: [], filterKey });
  }

  const goNext = useCallback((nextCursor: string | null) => {
    if (nextCursor === null) return;
    setState((previous) => ({
      ...previous,
      trail: [...previous.trail, previous.cursor],
      cursor: nextCursor,
    }));
  }, []);

  const goPrevious = useCallback(() => {
    setState((previous) => {
      const trail = [...previous.trail];
      const target = trail.pop();
      return { ...previous, trail, cursor: target };
    });
  }, []);

  const reset = useCallback(() => {
    setState((previous) => ({ ...previous, cursor: undefined, trail: [] }));
  }, []);

  return {
    cursor: state.cursor,
    canGoBack: state.trail.length > 0,
    goNext,
    goPrevious,
    reset,
  };
}

/**
 * Debounces a value, for search inputs.
 *
 * Without this every keystroke is a request, and because each response can arrive
 * out of order the list flickers between results for different prefixes.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(value);
    }, delayMs);
    return () => {
      clearTimeout(timer);
    };
  }, [value, delayMs]);

  return debounced;
}
